<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import DocumentGrid from './DocumentGrid.vue';
import DocumentTiles from './components/DocumentTiles.vue';
import DocumentPreview from './components/DocumentPreview.vue';
import FullWidthPaginationFooter from './components/FullWidthPaginationFooter.vue';
import ChunkToolbar from './components/ChunkToolbar.vue';
import RecordPreview from './components/RecordPreview.vue';
import EditKnowledgebase from './EditKnowledgebase.vue';
import AddSite from './AddSite.vue';
import AddPageVue from './AddPage.vue';
import AddMedia from './AddMedia.vue';
import KnowledgebaseHeader from './KnowledgebaseHeader.vue';
import Pane from '@/dashboard/components/Pane.vue';
import SchemaTool from './SchemaTool.vue';
import CommandPaletteSearch from './components/CommandPaletteSearch.vue';
import TemplateConfig from './components/TemplateConfig.vue';
import SitemapListModal from './components/SitemapListModal.vue';
import SitemapEditModal from './components/SitemapEditModal.vue';
import DragDropOverlay from './components/DragDropOverlay.vue';
import { useFileUpload } from './useFileUpload';
import { useBulkFileUpload } from './useBulkFileUpload';
import {
  isImmutableSchemaKnowledgebase,
  filterSpaceKbSchemaProperties,
} from './composables/useImmutableSchemaKnowledgebase';

const props = defineProps({
  knowledgebase: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  backUrl: {
    type: [String, Object],
    default: '',
  },
  backButtonLabel: {
    type: String,
    default: '',
  },
});

const store = useStore();

const schemaEditingDisabled = computed(() =>
  isImmutableSchemaKnowledgebase(props.knowledgebase)
);

const showEditPopup = ref(false);
const editKnowledgebaseRef = ref(null);

const dismissEditPopup = () => {
  showEditPopup.value = false;
};
const cancelEditPopup = () => {
  editKnowledgebaseRef.value?.resetForm();
  showEditPopup.value = false;
};
const handleEditModalClose = reason => {
  if (reason === 'cancel') cancelEditPopup();
  else dismissEditPopup();
};
const openEditPopup = () => {
  showEditPopup.value = true;
};
const showAddSitePopup = ref(false);
const showAddPagePopup = ref(false);
const showAddMediaPopup = ref(false);
const showSchemaTool = ref(false);
const isTileView = ref(props.knowledgebase?.library_type === 'gallery');
const showSearchDialog = ref(false);
const showTemplateConfig = ref(false);
const showSitemapListModal = ref(false);
const showSitemapEditModal = ref(false);
const editingSitemap = ref(null);

// Schema editing mode: when not showing the schema tool but we want to enable schema editing in the grid
const isSchemaEditMode = ref(false);

const selectedRows = ref([]);
const allRowsSelected = ref(false);
const visibleColumns = ref([]);
const availableColumns = ref([]);
const fileInputRef = ref(null);

const { uploadFile, attachFile } = useFileUpload();
const {
  addFilesToQueue,
  processQueue,
  isDragOver,
  isUploading,
  stats,
  progress,
} = useBulkFileUpload();

const isColumnDragging = ref(false);

const documents = computed(() => store.getters['documents/getDocuments']);
const documentsMeta = computed(() => store.getters['documents/getMeta']);

// Column filter state
const activeFilters = ref({});
const filteredDocuments = ref(null); // null means use normal documents

const hasActiveFilters = computed(() =>
  Object.values(activeFilters.value).some(f => f?.value)
);

const displayDocuments = computed(() => {
  if (hasActiveFilters.value && filteredDocuments.value !== null) {
    return filteredDocuments.value;
  }
  return documents.value;
});

// Simple client-side transform for search results (attaches metadata as top-level keys)
const transformSearchDocuments = docs => {
  return docs.map((doc, index) => ({
    id: doc.id || `search-${index}`,
    content: doc.page_content || doc.content,
    metadata: doc.metadata || {},
    record: doc.record || null,
    ...(doc.metadata || {}),
  }));
};

const handleColumnFilter = async ({ field, value }) => {
  if (value) {
    activeFilters.value = { ...activeFilters.value, [field]: { value } };
  } else {
    const updated = { ...activeFilters.value };
    delete updated[field];
    activeFilters.value = updated;
  }

  if (!hasActiveFilters.value) {
    filteredDocuments.value = null;
    return;
  }

  // Build must conditions from active filters based on field type
  const activeSchema = store.getters['knowledgebase/getActiveSchema'];
  const must = Object.entries(activeFilters.value)
    .filter(([, f]) => f?.value)
    .map(([f, { value: v }]) => {
      const fieldType = activeSchema?.properties?.[f]?.type;
      const numVal = Number(v);
      const isNumeric = !isNaN(numVal) && v.trim() !== '';

      // Use range for numeric fields or numeric-looking values
      if (
        fieldType === 'integer' ||
        fieldType === 'float' ||
        (isNumeric && fieldType !== 'boolean' && fieldType !== 'bool')
      ) {
        if (isNumeric) {
          return { key: f, range: { gte: numVal, lte: numVal } };
        }
      }
      if (fieldType === 'boolean' || fieldType === 'bool') {
        return { key: f, match: { value: v === 'true' } };
      }
      return { key: f, match: { text: v } };
    });

  try {
    const result = await store.dispatch('knowledgebase/searchDocumentsAction', {
      id: props.knowledgebase.id,
      query: Object.values(activeFilters.value)
        .map(f => f.value)
        .join(' '),
      maxResults: 20,
      searchType: 'vsearch',
      must,
    });

    if (result?.documents) {
      filteredDocuments.value = transformSearchDocuments(result.documents);
    } else {
      filteredDocuments.value = [];
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error('Filter search failed:', error);
      filteredDocuments.value = [];
    }
  }
};
const schema = computed(() => store.getters['knowledgebase/getActiveSchema']);
const isDeleting = computed(() => store.state.documents.uiFlags.isDeleting);
const isUpdating = computed(() => store.state.documents.uiFlags.isUpdating);
const records = computed(() => store.getters['records/getRecords']);

// Calculate processing and error counts
const processingCount = computed(() => {
  return records.value.filter(record => {
    const status = record.status?.toUpperCase();
    return ['SCHEDULED', 'PENDING', 'RUNNING'].includes(status);
  }).length;
});

const errorCount = computed(() => {
  return records.value.filter(record => {
    const status = record.status?.toUpperCase();
    return ['ERROR', 'FAILED'].includes(status);
  }).length;
});

const errorRecords = computed(() => {
  return records.value.filter(record => {
    const status = record.status?.toUpperCase();
    return ['ERROR', 'FAILED'].includes(status);
  });
});

const processingRecords = computed(() => {
  return records.value.filter(record => {
    const status = record.status?.toUpperCase();
    return ['SCHEDULED', 'PENDING', 'RUNNING'].includes(status);
  });
});

const hideAddSitePopup = () => {
  showAddSitePopup.value = false;
};

const hideAddPagePopup = () => {
  showAddPagePopup.value = false;
};

const hideAddMediaPopup = () => {
  showAddMediaPopup.value = false;
};

const handleAddSite = () => {
  showAddSitePopup.value = true;
};

const handleAddPage = () => {
  showAddPagePopup.value = true;
};

const handleAddMedia = () => {
  showAddMediaPopup.value = true;
};

const handleUploadFile = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const onFileChange = async event => {
  const files = Array.from(event.target.files);
  if (files.length === 0) return;

  if (files.length === 1) {
    // Single file: use existing upload logic
    const file = files[0];
    const upload = await uploadFile(file, props.knowledgebase.id);
    await attachFile({
      file,
      upload,
      knowledgebaseId: props.knowledgebase.id,
    });
    // Refresh documents
    await store.dispatch('documents/get', {
      knowledgebaseId: props.knowledgebase.id,
      offset: documentsMeta.value.offset,
      recordId: documentsMeta.value.record_id,
      limit: documentsMeta.value.limit,
    });
  } else {
    // Multiple files: use bulk upload queue
    addFilesToQueue(files, props.knowledgebase.id);
    // Auto-start processing after a short delay
    setTimeout(() => {
      processQueue();
    }, 100);
  }

  // Reset the file input
  event.target.value = '';
};

// Drag and drop handlers
const handleDragEnter = e => {
  // Don't activate file drop overlay if we're dragging a column
  if (isColumnDragging.value) {
    // Still need to prevent default to allow drop
    e.preventDefault();
    return;
  }

  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = true;
};

const handleDragOver = e => {
  // Always prevent default to allow drop events
  e.preventDefault();

  // Don't activate file drop overlay if we're dragging a column
  if (isColumnDragging.value) return;

  e.stopPropagation();
};

const handleDragLeave = e => {
  // Don't deactivate file drop overlay if we're dragging a column
  if (isColumnDragging.value) return;

  e.preventDefault();
  e.stopPropagation();
  // Only set to false if leaving the main container
  if (e.target.classList.contains('drag-drop-container')) {
    isDragOver.value = false;
  }
};

const handleDrop = async e => {
  // Don't handle file drop if we're dragging a column
  if (isColumnDragging.value) {
    // Still prevent default but don't process files
    e.preventDefault();
    return;
  }

  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = false;

  const files = Array.from(e.dataTransfer.files);
  if (files.length === 0) return;

  // Use bulk upload for all files
  addFilesToQueue(files, props.knowledgebase.id);
  setTimeout(() => {
    processQueue();
  }, 100);
};

const openSearchDialog = () => {
  showSearchDialog.value = true;
};

const hideSearchDialog = () => {
  showSearchDialog.value = false;
};

const handleSelectDocument = () => {
  // When a document is selected from search, keep the dialog open
  // so users can select multiple documents
  // Optionally, we could highlight the document in the grid here
  // or open it in a preview pane
};

const toggleSchemaTool = () => {
  if (schemaEditingDisabled.value) return;
  showSchemaTool.value = !showSchemaTool.value;
};

watch(schemaEditingDisabled, disabled => {
  if (disabled) {
    showSchemaTool.value = false;
    isSchemaEditMode.value = false;
  }
});

const toggleSchemaEditMode = () => {
  if (schemaEditingDisabled.value) return;
  isSchemaEditMode.value = !isSchemaEditMode.value;
};

const toggleViewMode = () => {
  isTileView.value = !isTileView.value;
};

const handleColumnDragStart = () => {
  isColumnDragging.value = true;
};

const handleColumnDragEnd = () => {
  isColumnDragging.value = false;
};

const openTemplateConfig = () => {
  showTemplateConfig.value = true;
};

const closeTemplateConfig = () => {
  showTemplateConfig.value = false;
};

const handleTemplateConfigSaved = () => {
  closeTemplateConfig();
};

const handleOpenSitemaps = () => {
  showSitemapListModal.value = true;
};

const hideSitemapListModal = () => {
  showSitemapListModal.value = false;
};

const handleEditSitemap = sitemap => {
  editingSitemap.value = sitemap;
  showSitemapEditModal.value = true;
};

const hideSitemapEditModal = () => {
  showSitemapEditModal.value = false;
  editingSitemap.value = null;
};

const handleSitemapSaved = () => {
  hideSitemapEditModal();
  store.dispatch('sitemaps/get', props.knowledgebase.id);
};

const closeRecordPane = () => {
  store.dispatch('records/clearRecord');
};

const handlePageChange = page => {
  // Calculate offset based on page number
  // For page 1, offset should be null (start from beginning)
  // For page 2+, we need to get the last document ID from the previous page
  let offset = null;
  if (page > 1) {
    const documentsOnCurrentPage = documents.value || [];

    // If we're going to the next page and have documents, use the last document's ID
    if (
      page === documentsMeta.value.current_page + 1 &&
      documentsOnCurrentPage.length > 0
    ) {
      offset = documentsOnCurrentPage[documentsOnCurrentPage.length - 1].id;
    }
    // For other cases (jumping to non-adjacent pages), we'll need to implement
    // proper offset calculation or let the backend handle it
  }

  store.dispatch('documents/get', {
    knowledgebaseId: props.knowledgebase.id,
    offset: offset,
    limit: documentsMeta.value.limit || 20,
    currentPage: page,
  });
};

const handleSelectionChanged = (rows, allSelected = false) => {
  selectedRows.value = rows;
  allRowsSelected.value = allSelected;
};

const handleDeleteRows = async selectedRowObjects => {
  if (!selectedRowObjects.length) return;

  // Extract document IDs from the selected row objects
  const documentIds = selectedRowObjects.map(row => row.id).filter(Boolean);

  if (!documentIds.length) return;

  try {
    await store.dispatch('documents/delete', {
      knowledgebaseId: props.knowledgebase.id,
      documentIds: documentIds,
    });

    // Clear selection after successful deletion
    selectedRows.value = [];

    // No need to refresh - the store mutation will update the documents array
    // and the DocumentGrid watcher will apply transactions automatically
  } catch (error) {
    // Handle error silently for now
    // You might want to show a user-friendly error message here
  }
};

const handleDeleteRecords = async recordIds => {
  if (!recordIds || !recordIds.length) return;

  const count = recordIds.length;
  const message =
    count === 1
      ? 'Are you sure you want to delete this source and all its documents? This action cannot be undone.'
      : `Are you sure you want to delete ${count} sources and all their documents? This action cannot be undone.`;

  // eslint-disable-next-line no-alert
  if (!confirm(message)) return;

  try {
    // Delete each record (which will cascade delete all its documents)
    await Promise.all(
      recordIds.map(recordId =>
        store.dispatch('records/delete', {
          knowledgebaseId: props.knowledgebase.id,
          recordId,
        })
      )
    );

    // Clear selection after successful deletion
    selectedRows.value = [];
  } catch (error) {
    // Handle error silently for now
    // eslint-disable-next-line no-console
    console.error('Failed to delete records:', error);
  }
};

const handleReindexRows = async selectedRowObjects => {
  if (!selectedRowObjects.length) return;

  // Get unique record IDs from selected documents
  const recordIds = [
    ...new Set(selectedRowObjects.map(row => row.record?.id).filter(Boolean)),
  ];

  if (!recordIds.length) return;

  const count = recordIds.length;
  const message =
    count === 1
      ? 'Are you sure you want to reindex this record? This will remove and reprocess all documents related to this record.'
      : `Are you sure you want to reindex ${count} records? This will remove and reprocess all documents related to these records.`;

  // eslint-disable-next-line no-alert
  if (!confirm(message)) return;

  try {
    // Reindex each unique record
    await Promise.all(
      recordIds.map(recordId =>
        store.dispatch('records/reindex', {
          knowledgebaseId: props.knowledgebase.id,
          recordId,
        })
      )
    );

    // Clear selection after successful reindex
    selectedRows.value = [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to reindex records:', error);
  }
};

const handleReindexAllRecords = async () => {
  // eslint-disable-next-line no-alert
  if (
    !confirm(
      'Are you sure you want to reindex all records? This will remove and reprocess all documents in this knowledge base.'
    )
  )
    return;

  try {
    await store.dispatch('records/reindexAll', {
      knowledgebaseId: props.knowledgebase.id,
    });

    // Clear selection after successful reindex
    selectedRows.value = [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to reindex all records:', error);
  }
};

const handleRetryFailedRecords = async failedRecords => {
  if (!failedRecords || failedRecords.length === 0) return;

  try {
    // Reindex each failed record in parallel
    await Promise.all(
      failedRecords.map(record =>
        store.dispatch('records/reindex', {
          knowledgebaseId: props.knowledgebase.id,
          recordId: record.id,
        })
      )
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to retry records:', error);
  }
};

const handleDeleteProcessingRecords = async processingRecordsToDelete => {
  if (!processingRecordsToDelete || processingRecordsToDelete.length === 0)
    return;

  // eslint-disable-next-line no-alert
  if (
    !confirm(
      `Are you sure you want to delete ${processingRecordsToDelete.length} processing record(s)? This will cancel their processing.`
    )
  )
    return;

  try {
    // Delete each processing record in parallel
    await Promise.all(
      processingRecordsToDelete.map(record =>
        store.dispatch('records/delete', {
          knowledgebaseId: props.knowledgebase.id,
          recordId: record.id,
        })
      )
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to delete processing records:', error);
  }
};

const handleDeleteFailedRecords = async failedRecordsToDelete => {
  if (!failedRecordsToDelete || failedRecordsToDelete.length === 0) return;

  // eslint-disable-next-line no-alert
  if (
    !confirm(
      `Are you sure you want to delete ${failedRecordsToDelete.length} failed record(s)?`
    )
  )
    return;

  try {
    // Delete each failed record in parallel
    await Promise.all(
      failedRecordsToDelete.map(record =>
        store.dispatch('records/delete', {
          knowledgebaseId: props.knowledgebase.id,
          recordId: record.id,
        })
      )
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to delete failed records:', error);
  }
};

const documentGridRef = ref(null);

const handlePreviewRow = selectedRowObject => {
  // Trigger the preview in DocumentGrid
  if (selectedRowObject && selectedRowObject.id && documentGridRef.value) {
    documentGridRef.value.handlePreview(selectedRowObject);
  }
};

// Document preview state and handlers
const selectedDocument = ref(null);
const selectedRecord = ref(null);

const hasDocument = computed(() => {
  return !!selectedDocument.value;
});

const hasSelectedRecord = computed(() => {
  return !!selectedRecord.value;
});

const handlePreview = async document => {
  selectedDocument.value = document;
};

const closeDocumentPreview = () => {
  selectedDocument.value = null;
};

const handleRecordClick = record => {
  selectedRecord.value = record;
};

const closeRecordPreview = () => {
  selectedRecord.value = null;
};

const getColumnStorageKey = () => {
  return `knowledgebase_columns_${props.knowledgebase.id}`;
};

const loadColumnVisibility = () => {
  const storageKey = getColumnStorageKey();
  const saved = localStorage.getItem(storageKey);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      // Ignore parsing error, use defaults
      return null;
    }
  }
  return null;
};

const saveColumnVisibility = columns => {
  const storageKey = getColumnStorageKey();
  localStorage.setItem(storageKey, JSON.stringify(columns));
};

const updateAvailableColumns = () => {
  if (!schema.value) return;

  const columns = Object.keys(
    filterSpaceKbSchemaProperties(schema.value.properties, props.knowledgebase)
  ).map(propertyKey => ({
    field: propertyKey,
    headerName: propertyKey.charAt(0).toUpperCase() + propertyKey.slice(1),
  }));

  // Add record column (from API, not schema)
  columns.push({
    field: 'record',
    headerName: 'Source',
  });

  // Add action column
  columns.push({
    field: 'actions',
    headerName: 'Actions',
  });

  availableColumns.value = columns;

  // Load saved visibility or default to all visible
  const saved = loadColumnVisibility();
  if (saved) {
    visibleColumns.value = saved;
  } else {
    visibleColumns.value = columns.map(col => col.field);
  }
};

const handleToggleColumn = columnField => {
  const index = visibleColumns.value.indexOf(columnField);
  if (index > -1) {
    visibleColumns.value.splice(index, 1);
  } else {
    visibleColumns.value.push(columnField);
  }

  // Save to localStorage
  saveColumnVisibility(visibleColumns.value);
};

const getName = () => {
  return props.knowledgebase?.name || 'Loading...';
};

const record = computed(() => store.getters['records/getActiveRecord']);
const hasRecord = computed(() => !!record.value);

// Load initial data
onMounted(() => {
  if (props.knowledgebase.id) {
    store.dispatch('records/get', props.knowledgebase.id);
    store.dispatch('documents/get', {
      knowledgebaseId: props.knowledgebase.id,
      currentPage: 1,
    });
    store.dispatch('knowledgebase/fetchActiveSchema', props.knowledgebase.id);
  }
});

// Watch for schema changes to update available columns
watch(
  schema,
  () => {
    if (schema.value) {
      updateAvailableColumns();
    }
  },
  { immediate: true }
);

// Watch for knowledgebase changes
watch(
  () => props.knowledgebase.id,
  () => {
    if (props.knowledgebase.id) {
      // Update tile view based on library type
      isTileView.value = props.knowledgebase?.library_type === 'gallery';
      store.dispatch('knowledgebase/clearActiveSchema');
      store.dispatch('records/get', props.knowledgebase.id);
      store.dispatch('documents/get', {
        knowledgebaseId: props.knowledgebase.id,
        offset: null,
        limit: 20,
        currentPage: 1,
      });
      store.dispatch('knowledgebase/fetchActiveSchema', props.knowledgebase.id);
    }
  }
);

// Watch for record deletions to remove associated documents from grid
watch(records, (newRecords, oldRecords) => {
  if (!oldRecords || !documentGridRef.value) return;

  // Find deleted records by comparing old and new record IDs
  const oldRecordIds = new Set(oldRecords.map(r => r.id));
  const newRecordIds = new Set(newRecords.map(r => r.id));

  // Find records that were in old but not in new (deleted)
  const deletedRecordIds = [...oldRecordIds].filter(
    id => !newRecordIds.has(id)
  );

  // Remove documents for each deleted record
  deletedRecordIds.forEach(recordId => {
    documentGridRef.value.removeDocumentsByRecordId(recordId);
  });
});

const handleSchemaChange = changeData => {
  // Handle schema changes from the grid
  // This could include refreshing data, showing notifications, etc.
  // eslint-disable-next-line no-console
  console.log('Schema change:', changeData);

  // Auto-refresh after schema change
  store.dispatch('knowledgebase/fetchActiveSchema', props.knowledgebase.id);
};
</script>

<template>
  <div
    class="w-full h-full flex flex-col drag-drop-container"
    @dragenter="handleDragEnter"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <DragDropOverlay :is-drag-over="isDragOver" />
    <KnowledgebaseHeader
      :knowledgebase="knowledgebase"
      :name="getName()"
      :show-back-button="showBackButton"
      :back-url="backUrl"
      :back-button-label="backButtonLabel"
      @edit="openEditPopup"
      @search="openSearchDialog"
    />
    <div class="flex flex-1 overflow-hidden min-w-0">
      <main class="flex-1 flex flex-col min-w-0">
        <ChunkToolbar
          :selected-rows="selectedRows"
          :all-rows-selected="allRowsSelected"
          :is-schema-active="showSchemaTool"
          :is-schema-edit-mode="isSchemaEditMode"
          :is-deleting="isDeleting"
          :is-updating="isUpdating"
          :is-tile-view="isTileView"
          :available-columns="availableColumns"
          :visible-columns="visibleColumns"
          :library-type="knowledgebase.library_type"
          :knowledgebase-id="knowledgebase.id"
          @toggle-schema-edit="toggleSchemaEditMode"
          @toggle-view-mode="toggleViewMode"
          @toggle-column="handleToggleColumn"
          @delete-rows="handleDeleteRows"
          @delete-records="handleDeleteRecords"
          @reindex-rows="handleReindexRows"
          @reindex-all-records="handleReindexAllRecords"
          @preview-row="handlePreviewRow"
          @open-template-config="openTemplateConfig"
          @add-site="handleAddSite"
          @add-page="handleAddPage"
          @add-media="handleAddMedia"
          @upload-file="handleUploadFile"
        />
        <div
          class="flex-1 min-h-0 overflow-hidden relative flex flex-col min-w-0"
        >
          <div v-if="showSchemaTool" class="flex-1 flex flex-col">
            <SchemaTool class="flex-1" :knowledgebase-id="knowledgebase.id" />
            <!-- Schema Footer -->
            <FullWidthPaginationFooter
              :current-page="1"
              :total-items="0"
              :items-per-page="20"
              :is-schema-active="showSchemaTool"
              :show-schema-toggle="!schemaEditingDisabled"
              @toggle-schema="toggleSchemaTool"
              @open-sitemaps="handleOpenSitemaps"
            />
          </div>

          <!-- Table View -->
          <DocumentGrid
            v-show="!showSchemaTool && !isTileView"
            ref="documentGridRef"
            :knowledgebase="knowledgebase"
            :filtered-documents="displayDocuments"
            :visible-columns="visibleColumns"
            :is-schema-edit-mode="isSchemaEditMode"
            :is-schema-active="showSchemaTool"
            :documents-meta="documentsMeta"
            :on-record-click="handleRecordClick"
            :processing-count="processingCount"
            :processing-records="processingRecords"
            :error-count="errorCount"
            :error-records="errorRecords"
            :is-uploading="isUploading"
            :upload-progress="progress"
            :upload-stats="stats"
            :active-filters="activeFilters"
            @selection-changed="handleSelectionChanged"
            @page-change="handlePageChange"
            @schema-change="handleSchemaChange"
            @toggle-schema="toggleSchemaTool"
            @retry-failed-records="handleRetryFailedRecords"
            @delete-processing-records="handleDeleteProcessingRecords"
            @delete-failed-records="handleDeleteFailedRecords"
            @open-sitemaps="handleOpenSitemaps"
            @column-drag-start="handleColumnDragStart"
            @column-drag-end="handleColumnDragEnd"
            @show-column="handleToggleColumn"
            @column-filter="handleColumnFilter"
          />

          <!-- Tile View -->
          <div
            v-show="!showSchemaTool && isTileView"
            class="flex-1 flex flex-col"
          >
            <div class="flex-1 overflow-y-auto">
              <DocumentTiles
                :documents="documents"
                @selection-changed="handleSelectionChanged"
                @preview-document="handlePreview"
              />
            </div>

            <!-- Pagination for Tile View with Schema Toggle -->
            <FullWidthPaginationFooter
              :current-page="documentsMeta.current_page"
              :total-items="documentsMeta.total"
              :items-per-page="documentsMeta.per_page"
              :is-schema-active="showSchemaTool"
              :show-schema-toggle="!schemaEditingDisabled"
              :processing-count="processingCount"
              :processing-records="processingRecords"
              :error-count="errorCount"
              :error-records="errorRecords"
              :is-uploading="isUploading"
              :upload-progress="progress"
              :upload-stats="stats"
              @update:current-page="handlePageChange"
              @toggle-schema="toggleSchemaTool"
              @retry-failed-records="handleRetryFailedRecords"
              @delete-processing-records="handleDeleteProcessingRecords"
              @delete-failed-records="handleDeleteFailedRecords"
              @open-sitemaps="handleOpenSitemaps"
            />
          </div>
        </div>
      </main>
    </div>

    <!-- Panes and Modals -->
    <Pane v-model:show="hasRecord" :on-close="closeRecordPane">
      <RecordPreview :record="record" />
    </Pane>

    <Pane v-model:show="hasSelectedRecord" :on-close="closeRecordPreview">
      <RecordPreview :record="selectedRecord" />
    </Pane>

    <Pane v-model:show="hasDocument" :on-close="closeDocumentPreview">
      <DocumentPreview
        :document="selectedDocument"
        :schema="schema"
        :knowledgebase="knowledgebase"
      />
    </Pane>

    <!-- Template Configuration Full Screen Pane -->
    <Pane
      v-model:show="showTemplateConfig"
      :on-close="closeTemplateConfig"
      full-screen
    >
      <TemplateConfig
        :knowledgebase-id="knowledgebase.id"
        @close="closeTemplateConfig"
        @saved="handleTemplateConfigSaved"
      />
    </Pane>

    <woot-modal
      v-model:show="showEditPopup"
      :destroy-on-close="false"
      :on-close="handleEditModalClose"
    >
      <EditKnowledgebase
        ref="editKnowledgebaseRef"
        :selected-knowledgebase="knowledgebase"
        @on-close="cancelEditPopup"
      />
    </woot-modal>

    <woot-modal v-model:show="showAddSitePopup" :on-close="hideAddSitePopup">
      <AddSite
        :selected-knowledgebase="knowledgebase"
        @on-close="hideAddSitePopup"
      />
    </woot-modal>

    <woot-modal v-model:show="showAddPagePopup" :on-close="hideAddPagePopup">
      <AddPageVue
        :selected-knowledgebase="knowledgebase"
        @on-close="hideAddPagePopup"
      />
    </woot-modal>

    <woot-modal
      v-model:show="showAddMediaPopup"
      :on-close="hideAddMediaPopup"
      full-width
    >
      <AddMedia
        :selected-knowledgebase="knowledgebase"
        @on-close="hideAddMediaPopup"
      />
    </woot-modal>

    <!-- Command Palette Search Dialog -->
    <CommandPaletteSearch
      :show="showSearchDialog"
      :knowledgebase-id="knowledgebase.id"
      @close="hideSearchDialog"
      @select="handleSelectDocument"
    />

    <woot-modal
      v-model:show="showSitemapListModal"
      :on-close="hideSitemapListModal"
      size="medium"
    >
      <SitemapListModal
        :knowledgebase-id="knowledgebase.id"
        @close="hideSitemapListModal"
        @edit-sitemap="handleEditSitemap"
      />
    </woot-modal>

    <woot-modal
      v-model:show="showSitemapEditModal"
      :on-close="hideSitemapEditModal"
    >
      <SitemapEditModal
        v-if="editingSitemap"
        :knowledgebase-id="knowledgebase.id"
        :sitemap="editingSitemap"
        @close="hideSitemapEditModal"
        @saved="handleSitemapSaved"
      />
    </woot-modal>

    <!-- Hidden file input for uploads -->
    <input
      ref="fileInputRef"
      type="file"
      multiple
      class="hidden"
      accept=".pdf,.csv,.txt,.docx,.doc,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.svg"
      @change="onFileChange"
    />
  </div>
</template>

<style scoped>
.button-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 0.625rem;
}
</style>
